import { useEffect, useState } from 'react';
import { FIGURE_MOUNT_WORDS } from '@boardflight/shared';
import type { AdminOrder, AdminOrders, OrderStatus } from '@boardflight/shared';
import { api, download, post } from '../api.js';
import { Async, Panel, Time, useAsync, useSelection } from '../ui.js';

/**
 * The figures shop's orders (docs/meshy-models-architecture.md §9.4, F4): what to print, paint and
 * post, newest paid first.
 *
 * Each order is a packing slip: its sets in each finish and bottom, every topper with the Base number
 * it goes on and the file it prints from on that bottom (the assembler's, the only one there is),
 * and how many TPU snap pieces go in the box
 * (docs/figure-plinths-plan.md §5.3). The address is not here and never will be: **Stripe**
 * opens the payment in Stripe's dashboard, where it is (§0.2). A refund is made there too, and the
 * order follows it here on its own. *Shipped* takes the tracking number; the buyer is emailed it by
 * the API's next hourly sweep.
 *
 * `#orders/<orderId>` — the link in the new-order email — scrolls to that order.
 */

const STATUS_WORDS: Record<OrderStatus, string> = {
  open: 'Unpaid',
  paid: 'Paid',
  making: 'Being made',
  shipped: 'Shipped',
  expired: 'Expired',
  refunded: 'Refunded',
};

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export function Orders() {
  const state = useAsync(() => api<AdminOrders>('/v1/admin/orders'), []);
  const [selected] = useSelection('orders');

  useEffect(() => {
    if (!selected || !state.data) return;
    document.getElementById(`order-${selected}`)?.scrollIntoView({ block: 'center' });
  }, [selected, state.data]);

  return (
    <Async state={state}>
      {(data) => {
        const open = data.orders.filter((order) => order.status === 'paid' || order.status === 'making');
        const done = data.orders.filter((order) => order.status !== 'paid' && order.status !== 'making');
        return (
          <>
            <Panel title="Sales">
              <p>
                <strong>{money(data.totals.thisMonthCents)}</strong> this month · {money(data.totals.lastMonthCents)} last month{' '}
                <span className="muted small">(items only, refunds left out; tax and shipping are Stripe’s)</span>
              </p>
            </Panel>
            <Panel title={`To make and ship (${open.length})`}>
              {open.length === 0 ? (
                <p className="muted">Nothing to make. A paid order lands here, and every operator is emailed.</p>
              ) : (
                <OrderTable orders={open} leadDays={data.leadDays} focused={selected} onChanged={state.reload} />
              )}
            </Panel>
            <Panel title="Shipped and refunded">
              {done.length === 0 ? (
                <p className="muted">None yet.</p>
              ) : (
                <OrderTable orders={done} leadDays={data.leadDays} focused={selected} onChanged={state.reload} />
              )}
            </Panel>
          </>
        );
      }}
    </Async>
  );
}

function OrderTable({
  orders,
  leadDays,
  focused,
  onChanged,
}: {
  orders: AdminOrder[];
  leadDays: number;
  focused: string | null;
  onChanged: () => void;
}) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Paid</th>
            <th>Buyer</th>
            <th>What</th>
            <th className="num">Items</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <OrderRow key={order.id} order={order} leadDays={leadDays} focused={order.id === focused} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrderRow({ order, leadDays, focused, onChanged }: { order: AdminOrder; leadDays: number; focused: boolean; onChanged: () => void }) {
  const [tracking, setTracking] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function act(path: string, body: unknown) {
    setBusy(true);
    setError(null);
    try {
      await post(path, body);
      onChanged();
    } catch (failure) {
      setError(String(failure));
    } finally {
      setBusy(false);
    }
  }

  const snapPieces = order.sets.reduce((sum, set) => sum + set.snapPieces, 0);

  return (
    <tr id={`order-${order.id}`} className={`order-row${order.late ? ' is-late' : ''}${focused ? ' focused' : ''}`}>
      <td className="nowrap">
        <Time at={order.paidAt ?? order.createdAt} />
        {order.late ? <div className="error small">Past {leadDays} days: write to the buyer</div> : null}
      </td>
      <td>{order.buyer ? `@${order.buyer}` : <span className="muted">account gone</span>}</td>
      <td>
        <a href={`#games/${order.game.id}`}>{order.game.name}</a>
        {order.sets.map((set) => (
          <div key={`${set.setId}-${set.finish}-${set.mount}`} className="small">
            <strong>
              {set.quantity} × {set.setName}
            </strong>
            , {set.finish === 'painted' ? 'hand-painted' : 'unpainted'}, {FIGURE_MOUNT_WORDS[set.mount].name.toLowerCase()}
            {set.snapPieces > 0 ? <span className="muted"> · {set.snapPieces} TPU snap {set.snapPieces === 1 ? 'piece' : 'pieces'}</span> : null}
            <ul className="order-figures">
              {set.figures.map((figure) => (
                <li key={figure.id}>
                  Base {figure.glyph}: {figure.name}, {FIGURE_MOUNT_WORDS[set.mount].name.toLowerCase()}
                  {figure.heightMm ? `, ${figure.heightMm} mm` : ''}{' '}
                  {figure.hasFile ? (
                    <button className="chip" onClick={() => void download(`/v1/admin/figures/${figure.id}/print/${set.mount}`).catch((failure) => setError(String(failure)))}>
                      3MF
                    </button>
                  ) : (
                    <span className="error">no file for this bottom</span>
                  )}
                  {set.finish === 'painted' ? (
                    <button className="chip" onClick={() => void download(`/v1/admin/figures/${figure.id}/files/painted.glb`).catch((failure) => setError(String(failure)))}>
                      Painted model
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ))}
        {snapPieces > 0 ? (
          <div className="small">
            <strong>Pack {snapPieces} TPU snap {snapPieces === 1 ? 'piece' : 'pieces'}</strong>, one under each snap-on topper.
          </div>
        ) : null}
      </td>
      <td className="num">{money(order.totalCents)}</td>
      <td>
        <span className={order.status === 'refunded' ? 'pill off' : order.status === 'shipped' ? 'pill on' : 'pill'}>{STATUS_WORDS[order.status]}</span>
        {order.tracking ? <div className="small muted">{order.tracking}</div> : null}
      </td>
      <td>
        <div className="order-actions">
          {order.stripeUrl ? (
            <a className="btn" href={order.stripeUrl} target="_blank" rel="noreferrer">
              Stripe
            </a>
          ) : null}
          {order.status === 'paid' ? (
            <button disabled={busy} onClick={() => void act(`/v1/admin/orders/${order.id}/making`, {})}>
              Making
            </button>
          ) : null}
          {order.status === 'paid' || order.status === 'making' ? (
            <form
              className="order-actions"
              onSubmit={(event) => {
                event.preventDefault();
                if (tracking.trim()) void act(`/v1/admin/orders/${order.id}/shipped`, { tracking: tracking.trim() });
              }}
            >
              <input className="credits-input" placeholder="Tracking number" value={tracking} onChange={(event) => setTracking(event.target.value)} />
              <button className="primary" type="submit" disabled={busy || tracking.trim() === ''}>
                Shipped
              </button>
            </form>
          ) : null}
        </div>
        {error ? <p className="error small">{error}</p> : null}
      </td>
    </tr>
  );
}
